import { Injectable } from '@angular/core';
import { MessageType } from '@shared/types';
import * as FileSaver from 'file-saver';
import { NgxFileDropEntry } from 'ngx-file-drop';
import * as XLSX from 'xlsx';
import { FileTabConfig } from '../components/upload-view-component/upload-view-component.type';
import { SnackBarService } from './snack-bar.service';

const EXCEL_TYPE =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

@Injectable({
    providedIn: 'root',
})
export class XlsxService {
    xlsx = XLSX;

    constructor(private readonly snackBarService: SnackBarService) {}

    public async handleWithUploadedFile<T>(
        files: NgxFileDropEntry[],
        mapFileKeys?: Record<string, string>,
        fileTabIndex?: string,
        parserFunction?: (rows: T[]) => T[],
        headerRow?: number
    ) {
        const rows: T[] = [];

        for (const file of files) {
            const fileEntry = <FileSystemFileEntry>file.fileEntry;
            const extractedRows = await this.extractRowsFromSheet<T>(
                fileEntry,
                fileTabIndex,
                headerRow
            );
            const finalRows = parserFunction
                ? parserFunction(extractedRows)
                : extractedRows;
            rows.push(...finalRows);
        }

        if (!mapFileKeys) {
            return rows;
        }

        const mappedRows = this.mapKeysFromXlsxFile(rows, mapFileKeys);
        return mappedRows;
    }

    public async handleMultiTabUploadedFile(
        files: NgxFileDropEntry[],
        tabConfigs: FileTabConfig<any>[]
    ): Promise<Map<string, any[]>> {
        const result = new Map<string, any[]>();

        for (const file of files) {
            const fileEntry = <FileSystemFileEntry>file.fileEntry;

            for (const tabConfig of tabConfigs) {
                try {
                    const extractedRows = await this.extractRowsFromSheet(
                        fileEntry,
                        tabConfig.tabName,
                        tabConfig.headerRow
                    );

                    const parsedRows = tabConfig.parserFunction
                        ? tabConfig.parserFunction(extractedRows)
                        : extractedRows;

                    const mappedRows = this.mapKeysFromXlsxFile(
                        parsedRows,
                        tabConfig.mapFileKeys
                    );

                    result.set(tabConfig.tabName, mappedRows);
                } catch (error) {
                    this.snackBarService.displayMessage(
                        `Erro ao processar aba ${tabConfig.tabName}: ${error}`,
                        MessageType.Error
                    );
                    throw error;
                }
            }
        }

        return result;
    }

    public getNamesFromFiles(files: NgxFileDropEntry[]) {
        const names: string[] = [];
        for (const file of files) {
            const { name } = <FileSystemFileEntry>file.fileEntry;
            names.push(name);
        }
        return names;
    }

    private async extractRowsFromSheet<T>(
        fileEntry: FileSystemFileEntry,
        fileTabIndex: string | undefined,
        headerRow?: number
    ): Promise<T[]> {
        return new Promise((resolve, reject) => {
            fileEntry.file(async (file: File) => {
                const rows = await this.getRowsFromXlsxFile<T>(
                    file,
                    fileTabIndex,
                    headerRow
                );
                if (!rows.length) {
                    reject();
                }
                resolve(rows);
            });
        });
    }

    private getRowsFromXlsxFile<T>(
        file: File,
        fileTabName: string | undefined,
        headerRow?: number
    ): Promise<T[]> {
        return new Promise((resolve, reject) => {
            file.arrayBuffer().then((buffer) => {
                const workbook: XLSX.WorkBook = this.xlsx.read(buffer, {
                    type: 'buffer',
                    cellDates: true,
                });

                const worksheetIndex = fileTabName
                    ? workbook.SheetNames.findIndex(
                          (value) => value === fileTabName
                      )
                    : 0;

                if (worksheetIndex < 0) {
                    this.snackBarService.displayMessage(
                        `Erro: Aba ${fileTabName} não encontrada no arquivo!`,
                        MessageType.Error
                    );
                    reject(
                        `Erro: Aba ${fileTabName} não encontrada no arquivo!`
                    );
                    return;
                }

                const worksheet =
                    workbook.Sheets[workbook.SheetNames[worksheetIndex]];

                const rows = this.xlsx.utils
                    .sheet_to_json<T>(worksheet, {
                        blankrows: false,
                        defval: '',
                        range: headerRow ?? 0,
                    })
                    .filter((row: T) =>
                        Object.values(row as keyof T).some(
                            (cell) => cell !== null && cell !== ''
                        )
                    );

                if (!rows.length) {
                    this.snackBarService.displayMessage(
                        'Erro: Nenhuma linha encontrada no arquivo!',
                        MessageType.Error
                    );
                    reject('Erro: Nenhuma linha encontrada no arquivo!');
                    return;
                }

                resolve(rows);
            });
        });
    }

    private mapKeysFromXlsxFile<T>(
        rows: T[],
        mapFileKeys: Record<string, string>
    ) {
        const mappedRows = rows.map((row) => {
            const mappedRow: Record<string, unknown> = {};

            for (const key in row) {
                const trimmedKey = key.trim();

                if (mapFileKeys[trimmedKey]) {
                    mappedRow[mapFileKeys[trimmedKey]] = row[key];
                }
            }
            return mappedRow;
        });

        return mappedRows;
    }

    public async extractFileFromUploadedFile(
        files: NgxFileDropEntry
    ): Promise<File> {
        const fileEntry = files.fileEntry as FileSystemFileEntry;

        return new Promise<File>((resolve, reject) => {
            fileEntry.file(
                (file: File) => {
                    if (!file) {
                        reject('File is null');
                    } else {
                        resolve(file);
                    }
                },
                (error) => {
                    reject(error);
                }
            );
        });
    }

    public downloadObjectListAsXlsxFile(
        arr: any[],
        excelFileName: string,
        mapFileKeys: Record<string, string>
    ): void {
        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
            arr.filter(Boolean).map((row) => {
                const newRow: Record<string, unknown> = {};
                Object.entries(mapFileKeys).forEach(
                    ([key, value]) => (newRow[key] = row[value])
                );
                return newRow;
            })
        );
        const workbook: XLSX.WorkBook = {
            Sheets: { data: worksheet },
            SheetNames: ['data'],
        };
        const excelBuffer: any = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array',
        });

        this.saveAsExcelFile(excelBuffer, excelFileName);
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
        FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
    }
}
